import React, { useState, useEffect } from "react";
import { useTheme } from "../context/ThemeContext";
import { Link } from "react-router-dom";
import "../App.css";

function Reports() {
  const { darkMode } = useTheme();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    fetchReports();
  }, [typeFilter]);

  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const typeParam = typeFilter ? `&report_type=${typeFilter}` : "";
      const response = await fetch(
        `/api/reports?limit=50&offset=0${typeParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Failed to fetch reports");
      const data = await response.json();
      setReports(data.reports);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (reportId, reportType, weekEnding) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/reports/${reportId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MarTech-Monitoring-${reportType}-${weekEnding || "report"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download report: " + err.message);
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      weekly:  { background: "#dbeafe", color: "#1e40af", border: "1px solid #3b82f6" },
      monthly: { background: "#e0e7ff", color: "#3730a3", border: "1px solid #6366f1" },
      pilot:   { background: "#fef3c7", color: "#92400e", border: "1px solid #f59e0b" },
      welcome: { background: "#dcfce7", color: "#166534", border: "1px solid #16a34a" },
    };
    const s = styles[type] || { background: "#f3f4f6", color: "#374151", border: "1px solid #9ca3af" };
    return (
      <span style={{
        ...s, padding: "2px 10px", borderRadius: "12px",
        fontSize: "0.75rem", fontWeight: 600, textTransform: "capitalize"
      }}>
        {type}
      </span>
    );
  };

  const getScoreBadge = (score) => {
    if (score === null || score === undefined) return "-";
    let color, bg;
    if (score >= 80) { color = "#166534"; bg = "#dcfce7"; }
    else if (score >= 60) { color = "#92400e"; bg = "#fef3c7"; }
    else { color = "#991b1b"; bg = "#fee2e2"; }
    return (
      <span style={{
        background: bg, color, padding: "2px 10px", borderRadius: "12px",
        fontSize: "0.85rem", fontWeight: 700
      }}>
        {score}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric"
    });
  };

  if (loading) {
    return <div className="loading">Loading reports...</div>;
  }

  return (
    <div>
      <div className="content-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ margin: "0 0 5px 0" }}>Reports</h1>
          <p style={{ margin: 0, color: "#6b7280" }}>
            Download your weekly strategic reports and monitoring summaries
          </p>
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{
            padding: "8px 14px", borderRadius: "8px", fontSize: "0.85rem",
            border: darkMode ? "1px solid #475569" : "1px solid #cbd5e1",
            background: darkMode ? "#334155" : "#f8fafc",
            color: darkMode ? "#e2e8f0" : "#334155",
            cursor: "pointer",
          }}
        >
          <option value="">All Report Types</option>
          <option value="weekly">Weekly Strategic</option>
          <option value="monthly">Monthly Summary</option>
          <option value="pilot">Pilot</option>
          <option value="welcome">Welcome</option>
        </select>
      </div>

      <div className="content-body">
        {error && (
          <div style={{
            padding: "12px", background: "#fecaca", color: "#991b1b",
            borderRadius: "6px", marginBottom: "20px",
          }}>
            {error}
          </div>
        )}

        {reports.length === 0 ? (
          <div className="card" style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>&#128202;</div>
            <h3 style={{ margin: "0 0 8px 0" }}>No Reports Yet</h3>
            <p style={{ color: "#6b7280", margin: 0, maxWidth: "400px", marginInline: "auto" }}>
              Your weekly strategic reports will appear here once monitoring is active.
              Reports are retained for 2 years.
            </p>
          </div>
        ) : (
          <div className="card">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: darkMode ? "2px solid #334155" : "2px solid #e2e8f0" }}>
                  {["Report", "Type", "Week Ending", "Health Score", "Generated", "Status", ""].map((h, i) => (
                    <th key={i} style={{
                      textAlign: i === 6 ? "right" : "left",
                      padding: "10px 12px", fontSize: "0.75rem", fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: "0.05em",
                      color: darkMode ? "#94a3b8" : "#64748b",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id} style={{
                    borderBottom: darkMode ? "1px solid #1e293b" : "1px solid #f1f5f9",
                  }}>
                    <td style={{ padding: "14px 12px" }}>
                      <Link to={`/reports/${report.id}`} style={{
                        fontWeight: 600, fontSize: "0.9rem",
                        color: darkMode ? "#90cdf4" : "#2b6cb0",
                        textDecoration: "none",
                        display: "block"
                      }}
                        onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                        onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                      >
                        {report.title}
                      </Link>
                      {report.company && (
                        <div style={{ fontSize: "0.8rem", color: darkMode ? "#64748b" : "#94a3b8", marginTop: "2px" }}>
                          {report.company}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "14px 12px" }}>{getTypeBadge(report.report_type)}</td>
                    <td style={{ padding: "14px 12px", color: darkMode ? "#cbd5e1" : "#475569", fontSize: "0.85rem" }}>
                      {formatDate(report.week_ending)}
                    </td>
                    <td style={{ padding: "14px 12px" }}>{getScoreBadge(report.health_score)}</td>
                    <td style={{ padding: "14px 12px", color: darkMode ? "#cbd5e1" : "#475569", fontSize: "0.85rem" }}>
                      {formatDate(report.created_at)}
                    </td>
                    <td style={{ padding: "14px 12px" }}>
                      <span style={{
                        padding: "2px 10px", borderRadius: "12px", fontSize: "0.75rem", fontWeight: 600,
                        background: report.status === "sent" ? "#dcfce7" : report.status === "validated" ? "#dbeafe" : "#f3f4f6",
                        color: report.status === "sent" ? "#166534" : report.status === "validated" ? "#1e40af" : "#374151",
                        textTransform: "capitalize",
                      }}>
                        {report.status}
                      </span>
                    </td>
                    <td style={{ padding: "14px 12px", textAlign: "right" }}>
                      <button
                        onClick={() => handleDownload(report.id, report.report_type, report.week_ending)}
                        className="btn btn-primary"
                        style={{ fontSize: "0.8rem", padding: "8px 16px" }}
                      >
                        Download PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;
